import { changed, noOp, rejected, type BrushSettings, type CommandExecution, type LiveCommand, type StrokePoint } from './commands';
import { damage } from './damage';
import { invertMatrix, transformPoint } from './geometry';
import { findLayer, layerDocumentTransform, type EditorDocument, type LayerId } from './model';
import { pointIsSelected } from './selection';
import type { RasterTileStore } from './tiles';

export type ExposureMode = 'dodge' | 'burn';
export type ExposureRange = 'shadows' | 'midtones' | 'highlights';

export interface ExposureSettings {
  exposure: number;
  range: ExposureRange;
  protectTones: boolean;
}

interface TileSnapshot {
  x: number;
  y: number;
  pixels: Uint8ClampedArray | null;
}

export function exposurePixel(
  color: readonly [number, number, number],
  mode: ExposureMode,
  settings: ExposureSettings,
  pressure = 1,
): [number, number, number] {
  const luminance = (color[0] * 0.2126 + color[1] * 0.7152 + color[2] * 0.0722) / 255;
  const rangeWeight = settings.range === 'shadows' ? (1 - luminance) ** 2
    : settings.range === 'highlights' ? luminance ** 2
      : Math.max(0, 1 - Math.abs(luminance - 0.5) * 2) ** 1.5;
  let amount = Math.max(0, Math.min(1, settings.exposure)) * Math.max(0, Math.min(1, pressure)) * rangeWeight;
  if (settings.protectTones) {
    const endpointGuard = mode === 'dodge' ? 1 - luminance : luminance;
    amount *= 0.35 + endpointGuard * 0.65;
  }
  return color.map((channel) => {
    const output = mode === 'dodge' ? channel + (255 - channel) * amount : channel * (1 - amount);
    return Math.round(Math.max(settings.protectTones ? 2 : 0, Math.min(settings.protectTones ? 253 : 255, output)));
  }) as [number, number, number];
}

export class ExposureStrokeCommand implements LiveCommand {
  readonly label: string;
  private readonly before = new Map<string, TileSnapshot>();
  private readonly after = new Map<string, TileSnapshot>();
  private lastPoint?: StrokePoint;

  constructor(
    private readonly layerId: LayerId,
    private readonly mode: ExposureMode,
    private readonly brush: BrushSettings,
    private readonly settings: ExposureSettings,
    private readonly firstPoint: StrokePoint,
  ) {
    this.label = mode === 'dodge' ? 'Dodge stroke' : 'Burn stroke';
  }

  retainedBytes(): number {
    return 256 + [...this.before.values(), ...this.after.values()].reduce((sum, tile) => sum + (tile.pixels?.byteLength ?? 0), 0);
  }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind !== 'raster') return rejected('Exposure target is not a raster layer.');
    if (layer.locked || layer.pixelsLocked) return rejected('Layer pixels are locked.');
    if (!invertMatrix(layerDocumentTransform(document.root, layer.id) ?? layer.transform)) return rejected('Layer transform is not invertible.');
    if (this.after.size) {
      this.restore(layer.raster, this.after);
      return changed(this.damage());
    }
    this.append(document, this.firstPoint);
    return changed(this.damage());
  }

  append(document: EditorDocument, point: StrokePoint): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind !== 'raster' || layer.locked || layer.pixelsLocked) return;
    const transform = layerDocumentTransform(document.root, layer.id) ?? layer.transform;
    const inverse = invertMatrix(transform);
    if (!inverse) return;
    const previous = this.lastPoint ?? point;
    const distance = Math.hypot(point.x - previous.x, point.y - previous.y);
    const step = Math.max(1, this.brush.size * Math.max(0.05, Math.min(1, this.brush.spacing)));
    const count = Math.max(1, Math.ceil(distance / step));
    for (let index = 1; index <= count; index += 1) {
      const amount = index / count;
      const sample = {
        x: previous.x + (point.x - previous.x) * amount,
        y: previous.y + (point.y - previous.y) * amount,
        pressure: previous.pressure + (point.pressure - previous.pressure) * amount,
      };
      this.dab(document, layer.raster, transform, inverse, sample);
    }
    this.lastPoint = point;
  }

  private dab(document: EditorDocument, store: RasterTileStore, transform: import('./geometry').Matrix, inverse: import('./geometry').Matrix, point: StrokePoint): void {
    const local = transformPoint(inverse, point);
    const radius = Math.max(0.5, Math.min(256, this.brush.size * (this.brush.pressureSize ? point.pressure : 1) / 2));
    const touched = new Set<string>();
    const minimumX = Math.max(0, Math.floor(local.x - radius));
    const maximumX = Math.min(store.width - 1, Math.ceil(local.x + radius));
    const minimumY = Math.max(0, Math.floor(local.y - radius));
    const maximumY = Math.min(store.height - 1, Math.ceil(local.y + radius));
    for (let y = minimumY; y <= maximumY; y += 1) for (let x = minimumX; x <= maximumX; x += 1) {
      const distance = Math.hypot(x + 0.5 - local.x, y + 0.5 - local.y) / radius;
      if (distance > 1) continue;
      const tileX = Math.floor(x / store.tileSize);
      const tileY = Math.floor(y / store.tileSize);
      const tile = store.getTile(tileX, tileY);
      if (!tile) continue;
      const dimensions = store.tileDimensions(tileX, tileY);
      const offset = ((y - tileY * store.tileSize) * dimensions.width + (x - tileX * store.tileSize)) * 4;
      if ((tile[offset + 3] ?? 0) === 0) continue;
      const documentPoint = transformPoint(transform, { x: x + 0.5, y: y + 0.5 });
      if (!pointIsSelected(document.selection, documentPoint)) continue;
      const hardness = Math.max(0, Math.min(1, this.brush.hardness));
      const falloff = distance <= hardness ? 1 : Math.max(0, 1 - (distance - hardness) / Math.max(0.001, 1 - hardness));
      const pressure = (this.brush.pressureOpacity === false ? 1 : point.pressure) * this.brush.opacity * this.brush.flow * falloff;
      if (pressure <= 0) continue;
      const key = `${tileX}:${tileY}`;
      if (!this.before.has(key)) this.before.set(key, { x: tileX, y: tileY, pixels: new Uint8ClampedArray(tile) });
      const output = exposurePixel([tile[offset] ?? 0, tile[offset + 1] ?? 0, tile[offset + 2] ?? 0], this.mode, this.settings, pressure);
      tile[offset] = output[0]; tile[offset + 1] = output[1]; tile[offset + 2] = output[2];
      touched.add(key);
    }
    for (const key of touched) {
      const [x, y] = key.split(':').map(Number);
      if (Number.isInteger(x) && Number.isInteger(y)) store.touchTile(x!, y!);
    }
  }

  seal(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind !== 'raster') return rejected('Exposure target no longer exists.');
    if (!this.before.size) return noOp();
    this.after.clear();
    for (const tile of this.before.values()) this.after.set(`${tile.x}:${tile.y}`, { x: tile.x, y: tile.y, pixels: new Uint8ClampedArray(layer.raster.getTile(tile.x, tile.y) ?? []) });
    return changed(this.damage());
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind === 'raster') this.restore(layer.raster, this.before);
  }

  redo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind === 'raster') this.restore(layer.raster, this.after);
  }

  private restore(store: RasterTileStore, snapshots: ReadonlyMap<string, TileSnapshot>): void {
    for (const tile of snapshots.values()) store.setTile(tile.x, tile.y, tile.pixels);
  }

  damage() {
    return damage({ layerIds: [this.layerId], tiles: [...this.before.values()].map((tile) => ({ layerId: this.layerId, x: tile.x, y: tile.y, target: 'pixels' as const })) });
  }
}
